import { useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface MediaType {
  id?: string;
  display_time?: number;
  title?: string;
  [key: string]: any;
}
type SetIndex = React.Dispatch<React.SetStateAction<number>>;

// Função para registrar exibição de mídia (não-bloqueante)
const logPlayback = async (
  media: MediaType,
  displayTimeMs: number,
  deviceId?: string,
  screenId?: string,
  userId?: string,
  deviceName?: string,
  screenName?: string
) => {
  // Não bloquear a navegação - executar em background
  try {
    // Obter userId do sessionStorage se não fornecido
    const effectiveUserId = userId || sessionStorage.getItem('player_activated_user_id');

    if (!effectiveUserId || !media.id) {
      return; // Não logar sem userId ou mediaId
    }

    // Obter deviceId/screenId do sessionStorage se não fornecidos
    const effectiveDeviceId = deviceId || sessionStorage.getItem('player_device_id');
    const effectiveScreenId = screenId || localStorage.getItem('player_screen_id');

    // Inserir log de forma assíncrona
    supabase
      .from('media_playback_logs')
      .insert({
        media_id: media.id,
        device_id: effectiveDeviceId || null,
        screen_id: effectiveScreenId || null,
        user_id: effectiveUserId,
        media_title: media.title || null,
        device_name: deviceName || localStorage.getItem('player_device_name') || null,
        screen_name: screenName || localStorage.getItem('player_screen_name') || null,
        display_time_ms: displayTimeMs,
        duration_ms: displayTimeMs,
        started_at: new Date(Date.now() - displayTimeMs).toISOString(),
        ended_at: new Date().toISOString(),
      })
      .then(({ error }) => {
        if (error) {
          // Log silencioso - não interferir no player
          console.debug('[INSIGHTS] Erro ao registrar playback:', error.message);
        }
      });
  } catch (e) {
    // Ignorar erros - não interferir no player
    console.debug('[INSIGHTS] Erro ao registrar playback:', e);
  }
};

export const useMediaNavigation = (
  filteredMedias: MediaType[],
  currentMediaIndex: number,
  setCurrentMediaIndex: SetIndex
) => {
  // Ref para rastrear qual mídia foi exibida e por quanto tempo
  const lastMediaRef = useRef<{ media: MediaType; startTime: number } | null>(null);

  useEffect(() => {
    if (filteredMedias.length > 1) {
      const currentMedia = filteredMedias[currentMediaIndex];
      const mediaDisplayTime = currentMedia?.display_time;

      // Se mudou de mídia, registrar a exibição anterior
      if (lastMediaRef.current && lastMediaRef.current.media.id !== currentMedia?.id) {
        const actualDisplayTime = Date.now() - lastMediaRef.current.startTime;
        logPlayback(
          lastMediaRef.current.media,
          actualDisplayTime
        );
      }

      // Atualizar referência para mídia atual
      lastMediaRef.current = {
        media: currentMedia,
        startTime: Date.now()
      };

      if (mediaDisplayTime === 0) {
        return; // Não configura timer para mídia fixa
      }

      const effectiveDisplayTime = mediaDisplayTime || 5000; // Padrão de 5 segundos se não especificado
      console.log(`[PLAYER] ⏱️ Tempo de exibição para a mídia '${currentMedia?.title || currentMedia?.id}': ${effectiveDisplayTime}ms`);

      const timeout = setTimeout(() => {
        const nextIndex = (currentMediaIndex + 1) % filteredMedias.length;
        setCurrentMediaIndex(nextIndex);
      }, effectiveDisplayTime);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [currentMediaIndex, filteredMedias, setCurrentMediaIndex]);

  // Limpar ao desmontar - registrar última mídia exibida
  useEffect(() => {
    return () => {
      if (lastMediaRef.current) {
        const actualDisplayTime = Date.now() - lastMediaRef.current.startTime;
        logPlayback(lastMediaRef.current.media, actualDisplayTime);
      }
    };
  }, []);
};
